import React, { useRef, useState } from "react";
import { Badge, Box, Button, Checkbox, Text, useOutsideClick, VStack } from "@chakra-ui/react";
import { CaretDown } from "@phosphor-icons/react";

// Multi-select filter pill — mirrors the Data Collection TaskFilterBar pill:
// outline button that turns lime when a selection is active, with a count
// badge and a checkbox dropdown. `options` are { value, label }; `selected`
// is the array of selected option objects.
function FilterPill({ label, options, selected, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef();
  useOutsideClick({ ref, handler: () => setIsOpen(false) });

  const hasSelection = selected.length > 0;

  return (
    <Box ref={ref} position="relative">
      <Button
        size="sm"
        borderRadius="full"
        variant="outline"
        borderColor={hasSelection ? "lime" : "gray.200"}
        bg={hasSelection ? "lightLime" : "white"}
        color="gray.700"
        fontWeight="500"
        fontSize="13px"
        rightIcon={<CaretDown size={12} />}
        onClick={() => setIsOpen(!isOpen)}
        _hover={{ borderColor: "lime" }}
      >
        {label}
        {hasSelection && (
          <Badge ml={1} borderRadius="full" fontSize="10px" colorScheme="green" variant="solid" px={1.5}>
            {selected.length}
          </Badge>
        )}
      </Button>

      {isOpen && (
        <Box
          position="absolute"
          top="calc(100% + 4px)"
          left={0}
          zIndex={10}
          bg="white"
          border="1px solid"
          borderColor="gray.200"
          borderRadius="lg"
          boxShadow="md"
          minW="220px"
          maxH="260px"
          overflowY="auto"
          py={2}
          sx={{
            "&::-webkit-scrollbar": { width: "6px" },
            "&::-webkit-scrollbar-thumb": { bg: "transparent", borderRadius: "full" },
            "&:hover::-webkit-scrollbar-thumb": { bg: "gray.300" },
            scrollbarWidth: "thin",
            scrollbarColor: "transparent transparent",
            "&:hover": { scrollbarColor: "var(--chakra-colors-gray-300) transparent" },
          }}
        >
          <VStack align="start" spacing={0} px={3}>
            {options.map((option) => (
              <Checkbox
                key={option.value}
                size="sm"
                colorScheme="limeShade"
                isChecked={selected.some((s) => s.value === option.value)}
                onChange={(e) => {
                  if (e.target.checked) {
                    onChange([...selected, option]);
                  } else {
                    onChange(selected.filter((s) => s.value !== option.value));
                  }
                }}
                py={1.5}
                w="100%"
              >
                <Text fontSize="13px" color="gray.600">
                  {option.label}
                </Text>
              </Checkbox>
            ))}
          </VStack>
        </Box>
      )}
    </Box>
  );
}

export default FilterPill;
